/**
 * Equipment Tracker - Manage Users
 * @author Omid Zaeri (zaerio)
 */

import React, { useEffect, useState } from 'react';
import {
  Table, Header, SpaceBetween, Button, Modal, FormField, Input, Select, Box, Badge, Alert,
} from '@cloudscape-design/components';
import { getUsers, getManagers, createUser, updateUser, grantAdmin, revokeAdmin, deleteUser, lookupUser, triggerLookup } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TIER_RANK = { 'Regional Manager': 5, 'Manager': 4, 'Engineer': 3, 'Technician': 2, 'EC': 1 };

export default function ManageUsers() {
  const { isAdmin, user: currentUser, isImpersonating } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [managerFilter, setManagerFilter] = useState(null);
  const isSuperAdmin = currentUser?.alias === 'zaerio';
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState({ alias: '', name: '', email: '', tier: '', slackUserId: '', jobTitle: '', manager: '' });
  const [submitting, setSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ visible: false, alias: '', equipment: [] });
  const [deleting, setDeleting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [lookupCountdown, setLookupCountdown] = useState(0);
  const [editModal, setEditModal] = useState({ visible: false, alias: '' });
  const [editForm, setEditForm] = useState({ name: '', email: '', tier: '' });
  const [saving, setSaving] = useState(false);

  const defaultTiers = ['Manager', 'Regional Manager', 'Engineer', 'Technician', 'EC'];
  const [tierOptions, setTierOptions] = useState(defaultTiers.map(t => ({ value: t, label: t })));

  // Map phonetool job_title to a tier. If no match, create a new tier from the title.
  const mapJobTitleToTier = (jobTitle) => {
    if (!jobTitle) return '';
    const title = jobTitle.toLowerCase();
    // Match known tiers by keyword
    if (title.includes('manager') || title.includes('director') || title.includes('principal')) return 'Manager';
    if (title.includes('associate')) return 'Technician';
    if (title.includes('engineer') || title.includes('developer') || title.includes('sde')) return 'Engineer';
    if (title.includes('technician') || title.includes('tech ')) return 'Technician';
    if (title.includes('contractor') || title.includes(' ec') || title.includes('contingent')) return 'EC';
    // No match — use the job title itself as a new tier
    const newTier = jobTitle.trim();
    setTierOptions((prev) => {
      if (prev.some(o => o.value === newTier)) return prev;
      return [...prev, { value: newTier, label: newTier }];
    });
    return newTier;
  };

  const handleAliasLookup = async () => {
    const alias = form.alias.trim();
    if (!alias) return;
    setLookingUp(true);
    setLookupFailed(false);

    // Step 1: Try direct lookup from cache
    try {
      const pt = await lookupUser(alias);
      if (!pt.lookupFailed && pt.name) {
        const tier = mapJobTitleToTier(pt.title || '');
        setForm((prev) => ({
          ...prev, alias: pt.alias || alias, name: pt.name || prev.name,
          email: pt.email || `${alias}@amazon.com`, tier: tier || prev.tier,
          jobTitle: pt.title || '', manager: pt.manager || '',
        }));
        setLookingUp(false);
        return;
      }
    } catch { /* not in cache, continue */ }

    // Step 2: Trigger pending lookup for the Cloud Desktop worker
    await triggerLookup(null, alias).catch(() => null);

    // Step 3: Wait 10 seconds with countdown
    setLookupCountdown(10);
    await new Promise(resolve => {
      let remaining = 10;
      const timer = setInterval(() => {
        remaining -= 1;
        setLookupCountdown(remaining);
        if (remaining <= 0) { clearInterval(timer); resolve(); }
      }, 1000);
    });
    setLookupCountdown(0);

    // Step 4: Try lookup again (should be resolved by worker now)
    try {
      const pt = await lookupUser(alias);
      if (!pt.lookupFailed && pt.name) {
        const tier = mapJobTitleToTier(pt.title || '');
        setForm((prev) => ({
          ...prev, alias: pt.alias || alias, name: pt.name || prev.name,
          email: pt.email || `${alias}@amazon.com`, tier: tier || prev.tier,
          jobTitle: pt.title || '', manager: pt.manager || '',
        }));
      } else {
        setForm((prev) => ({ ...prev, email: pt.email || `${alias}@amazon.com` }));
        setLookupFailed(true);
      }
    } catch (err) {
      console.error('User lookup failed:', err.message);
      setForm((prev) => ({ ...prev, email: prev.email || `${alias}@amazon.com` }));
      setLookupFailed(true);
    } finally {
      setLookingUp(false);
    }
  };

  const loadUsers = () => {
    setLoading(true);
    const viewAs = isImpersonating ? currentUser?.alias : undefined;
    getUsers(managerFilter?.value || undefined, viewAs).then(setUsers).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { if (isAdmin) loadUsers(); }, [isAdmin, managerFilter, isImpersonating, currentUser?.alias]);

  // Load managers list for super admin filter
  useEffect(() => {
    if (isSuperAdmin) {
      getManagers().then(setManagers).catch(console.error);
    }
  }, [isSuperAdmin]);

  if (!isAdmin) return <Box padding="l"><p>Admin access required.</p></Box>;

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await createUser(form);
      setShowAddModal(false);
      setForm({ alias: '', name: '', email: '', tier: '', slackUserId: '', jobTitle: '', manager: '' });
      loadUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleAdmin = async (alias, currentRole) => {
    try {
      if (currentRole === 'admin') {
        await revokeAdmin(alias);
      } else {
        await grantAdmin(alias);
      }
      loadUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (alias) => {
    try {
      const result = await deleteUser(alias);
      if (result.needsConfirmation) {
        setDeleteModal({ visible: true, alias, equipment: result.assignedEquipment || [] });
        return;
      }
      loadUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const confirmDeleteUser = async () => {
    setDeleting(true);
    try {
      await deleteUser(deleteModal.alias, true);
      setDeleteModal({ visible: false, alias: '', equipment: [] });
      loadUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (user) => {
    setEditForm({ name: user.name, email: user.email, tier: user.tier, slackUserId: user.slackUserId || '' });
    setEditModal({ visible: true, alias: user.alias });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await updateUser(editModal.alias, editForm);
      setEditModal({ visible: false, alias: '' });
      loadUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SpaceBetween size="l">
      <Table
        loading={loading}
        items={users}
        header={
          <Header variant="h1" actions={<Button variant="primary" onClick={() => setShowAddModal(true)}>Add User</Button>}>
            Manage Users
          </Header>
        }
        filter={isSuperAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#525252', whiteSpace: 'nowrap' }}>Filter by manager:</span>
            <div style={{ width: 280 }}>
              <Select
                selectedOption={managerFilter}
                onChange={({ detail }) => setManagerFilter(detail.selectedOption?.value ? detail.selectedOption : null)}
                options={[
                  { value: '', label: 'All Users' },
                  ...managers.map(m => ({ value: m.alias, label: `${m.name} (${m.alias})`, description: m.tier })),
                ]}
                placeholder="All Users"
                filteringType="auto"
                expandToViewport
              />
            </div>
          </div>
        )}
        columnDefinitions={[
          { id: 'alias', header: 'Alias', cell: (u) => u.alias },
          { id: 'name', header: 'Name', cell: (u) => u.name },
          { id: 'email', header: 'Email', cell: (u) => u.email },
          { id: 'tier', header: 'Tier', cell: (u) => <Badge>{u.tier}</Badge> },
          { id: 'role', header: 'Role', cell: (u) => <Badge color={u.role === 'admin' ? 'blue' : 'grey'}>{u.role}</Badge> },
          { id: 'slack', header: 'Slack', cell: (u) => u.slackUserId ? <Badge color="green">✓</Badge> : <Badge color="grey">—</Badge> },
          {
            id: 'actions',
            header: 'Actions',
            cell: (u) => {
              const canRemove = u.alias !== 'zaerio' && currentUser?.alias !== u.alias &&
                (currentUser?.alias === 'zaerio' || (TIER_RANK[currentUser?.tier] || 0) > (TIER_RANK[u.tier] || 0));
              return u.alias === 'zaerio' ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <span>Super Admin</span>
                  <Button variant="link" onClick={() => openEditModal(u)}>Edit</Button>
                </SpaceBetween>
              ) : (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => openEditModal(u)}>Edit</Button>
                  <Button variant="link" onClick={() => handleToggleAdmin(u.alias, u.role)}>
                    {u.role === 'admin' ? 'Revoke Admin' : 'Grant Admin'}
                  </Button>
                  {canRemove && (
                    <Button variant="link" onClick={() => handleDeleteUser(u.alias)}>
                      Remove
                    </Button>
                  )}
                </SpaceBetween>
              );
            },
          },
        ]}
        empty={<Box textAlign="center" padding="l"><b>No users found</b></Box>}
      />

      <Modal
        visible={showAddModal}
        onDismiss={() => setShowAddModal(false)}
        header="Add User"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleCreate} loading={submitting}>Add User</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          <FormField label="Alias" description="Enter alias and press Tab to auto-fill name and email">
            <Input
              value={form.alias}
              onChange={({ detail }) => setForm({ ...form, alias: detail.value })}
              onBlur={handleAliasLookup}
              placeholder="e.g. johndoe"
              disabled={lookingUp}
            />
          </FormField>
          {lookupCountdown > 0 && (
            <Alert type="info">Verification in progress... {lookupCountdown}s</Alert>
          )}
          <FormField label="Name" description={lookupFailed ? 'Could not auto-fill. Please enter manually.' : ''}>
            <Input value={form.name} onChange={({ detail }) => setForm({ ...form, name: detail.value })} disabled={lookingUp} placeholder={lookingUp ? 'Looking up...' : 'Full name'} />
          </FormField>
          <FormField label="Email">
            <Input value={form.email} onChange={({ detail }) => setForm({ ...form, email: detail.value })} type="email" disabled={lookingUp} placeholder={lookingUp ? 'Looking up...' : ''} />
          </FormField>
          <FormField label="Tier">
            <Select
              selectedOption={form.tier ? { value: form.tier, label: form.tier } : null}
              onChange={({ detail }) => setForm({ ...form, tier: detail.selectedOption.value })}
              options={tierOptions}
            />
          </FormField>
          <FormField label="Slack User ID" description="Optional. Found in Slack profile → ⋮ → Copy member ID (e.g. U012AB3CD)">
            <Input value={form.slackUserId} onChange={({ detail }) => setForm({ ...form, slackUserId: detail.value })} placeholder="U012AB3CD" />
          </FormField>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={deleteModal.visible}
        onDismiss={() => setDeleteModal({ visible: false, alias: '', equipment: [] })}
        header="Confirm User Removal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setDeleteModal({ visible: false, alias: '', equipment: [] })}>Cancel</Button>
              <Button variant="primary" onClick={confirmDeleteUser} loading={deleting}>
                Confirm — All Equipment Returned
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning">
            User <strong>{deleteModal.alias}</strong> has {deleteModal.equipment.length} assigned equipment item(s).
            Please confirm that all equipment has been returned before removing this user.
          </Alert>
          <Table
            items={deleteModal.equipment}
            columnDefinitions={[
              { id: 'type', header: 'Type', cell: (e) => e.type },
              { id: 'serialNumber', header: 'Serial Number', cell: (e) => e.serialNumber || '-' },
            ]}
            variant="embedded"
          />
        </SpaceBetween>
      </Modal>

      <Modal
        visible={editModal.visible}
        onDismiss={() => setEditModal({ visible: false, alias: '' })}
        header="Edit User"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setEditModal({ visible: false, alias: '' })}>Cancel</Button>
              <Button variant="primary" onClick={handleSaveEdit} loading={saving}>Save Changes</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          <FormField label="Alias">
            <Input value={editModal.alias} disabled={true} />
          </FormField>
          <FormField label="Name">
            <Input value={editForm.name} onChange={({ detail }) => setEditForm({ ...editForm, name: detail.value })} />
          </FormField>
          <FormField label="Email">
            <Input value={editForm.email} onChange={({ detail }) => setEditForm({ ...editForm, email: detail.value })} type="email" />
          </FormField>
          <FormField label="Tier">
            <Select
              selectedOption={editForm.tier ? { value: editForm.tier, label: editForm.tier } : null}
              onChange={({ detail }) => setEditForm({ ...editForm, tier: detail.selectedOption.value })}
              options={tierOptions}
            />
          </FormField>
          <FormField label="Slack User ID" description="Optional. Found in Slack profile → ⋮ → Copy member ID (e.g. U012AB3CD)">
            <Input value={editForm.slackUserId || ''} onChange={({ detail }) => setEditForm({ ...editForm, slackUserId: detail.value })} placeholder="U012AB3CD" />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
